import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import { FolderZip } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { getAuthToken } from '../../lib/authToken';

export interface ExportCaseFileDialogProps {
  open: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber?: string;
}

type ExportOptions = {
  documentsOnly: boolean;
  includeDocuments: boolean;
  includePrivilegeLog: boolean;
  includeParties: boolean;
  includeProperties: boolean;
  includeIntake: boolean;
  includeNotes: boolean;
  includeBilling: boolean;
  includeCallsMeetings: boolean;
  includeConfidential: boolean;
  includeWithheldPrivilegeDocs: boolean;
  applySendGuard: boolean;
  piiAttestation: boolean;
};

const defaultOptions: ExportOptions = {
  documentsOnly: false,
  includeDocuments: true,
  includePrivilegeLog: true,
  includeParties: true,
  includeProperties: true,
  includeIntake: true,
  includeNotes: true,
  includeBilling: false,
  includeCallsMeetings: false,
  includeConfidential: false,
  includeWithheldPrivilegeDocs: false,
  applySendGuard: true,
  piiAttestation: false,
};

const ExportCaseFileDialog: React.FC<ExportCaseFileDialogProps> = ({
  open,
  onClose,
  caseId,
  caseNumber,
}) => {
  const [options, setOptions] = useState<ExportOptions>(defaultOptions);
  const [preview, setPreview] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [exporting, setExporting] = useState(false);

  const setFlag = (key: keyof ExportOptions, value: boolean) => {
    setOptions((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (!open) {
      setOptions(defaultOptions);
      setPreview(null);
      return;
    }
  }, [open]);

  const runPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await fetch(`/api/case-packages/${caseId}/preview`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options),
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Preview failed'));
      }
      const json = await res.json();
      setPreview(json.data);
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Unable to preview export'));
    } finally {
      setLoadingPreview(false);
    }
  };

  const runExport = async () => {
    if (!options.piiAttestation) {
      toast.error('Confirm the PII / confidentiality attestation to continue');
      return;
    }
    setExporting(true);
    try {
      const res = await fetch(`/api/case-packages/${caseId}/export`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options),
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Export failed'));
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match?.[1]
        || `argislegal-case-${caseNumber || caseId}.zip`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Case package downloaded — handle as confidential');
      onClose();
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Export case file {caseNumber ? `— ${caseNumber}` : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">
            Exports contain personally identifiable information (PII) and may include
            privileged material metadata. Download only when authorized, store under
            firm confidentiality controls, and never place on unsecured consumer cloud
            storage.
          </Alert>

          <FormControlLabel
            control={
              <Checkbox
                checked={options.documentsOnly}
                onChange={(e) => setFlag('documentsOnly', e.target.checked)}
              />
            }
            label="Documents ZIP only (Clio-style file package)"
          />

          {!options.documentsOnly && (
            <FormGroup>
              <Typography variant="subtitle2" gutterBottom>
                Include in ArgisLegal Case Package
              </Typography>
              {(
                [
                  ['includeDocuments', 'Documents (native files)'],
                  ['includePrivilegeLog', 'Privilege log'],
                  ['includeParties', 'Parties'],
                  ['includeProperties', 'Properties'],
                  ['includeIntake', 'Intake / consents'],
                  ['includeNotes', 'Case notes'],
                  ['includeBilling', 'Billing / time (financial PII — opt-in)'],
                  ['includeCallsMeetings', 'Calls / meetings'],
                ] as const
              ).map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={options[key]}
                      onChange={(e) => setFlag(key, e.target.checked)}
                    />
                  }
                  label={label}
                />
              ))}
            </FormGroup>
          )}

          <FormGroup>
            <Typography variant="subtitle2" gutterBottom>
              Protocol gates
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={options.applySendGuard}
                  onChange={(e) => setFlag('applySendGuard', e.target.checked)}
                />
              }
              label="Apply outbound send-guard (merge fields + attorney review policy)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={options.includeConfidential}
                  onChange={(e) => setFlag('includeConfidential', e.target.checked)}
                />
              }
              label="Include confidential documents/notes"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={options.includeWithheldPrivilegeDocs}
                  onChange={(e) => setFlag('includeWithheldPrivilegeDocs', e.target.checked)}
                />
              }
              label="Include privilege-withheld document files (attorney confirmation)"
            />
          </FormGroup>

          <FormControlLabel
            control={
              <Checkbox
                checked={options.piiAttestation}
                onChange={(e) => setFlag('piiAttestation', e.target.checked)}
                color="warning"
              />
            }
            label="I attest this export is authorized and will be handled under firm confidentiality and ethics rules."
          />

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              onClick={runPreview}
              disabled={loadingPreview}
              startIcon={loadingPreview ? <CircularProgress size={16} /> : undefined}
            >
              Preview counts
            </Button>
          </Box>

          {preview && (
            <Alert severity="info">
              Documents included: {preview.documentIncluded} / {preview.documentTotal}
              {preview.documentSkipped > 0
                ? ` · skipped ${preview.documentSkipped} (guard / privilege / confidential)`
                : ''}
              {preview.protocol?.attorneyReviewRequired
                ? ' · attorney review required by tenant policy'
                : ''}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FolderZip />}
          onClick={runExport}
          disabled={exporting || !options.piiAttestation}
        >
          {exporting ? 'Building…' : 'Download package'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportCaseFileDialog;
